import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Button,
  CircularProgress,
  Chip,
  TextField,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Target,
  Trash2,
  RefreshCw,
  Copy,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Calendar,
  Search,
} from 'lucide-react';
import api from '../services/api';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

// Returns array of 7 Date objects starting from the ISO week_start_date string
const getWeekDates = (weekStartDate) => {
  if (!weekStartDate) return Array(7).fill(null);
  const base = new Date(weekStartDate + 'T00:00:00');
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(d.getDate() + i);
    return d;
  });
};

const fmtDay = (date) => {
  if (!date) return '';
  return (date.getMonth() + 1).toString().padStart(2, '0') + '/' + date.getDate().toString().padStart(2, '0');
};

const fmt = (n) =>
  n == null || n === 0
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);

const WeekBadge = ({ type }) => {
  const map = {
    current: { label: 'Current', color: 'primary' },
    future: { label: 'Future', color: 'info' },
    past: { label: 'Past', color: 'default' },
  };
  const { label, color } = map[type] || map.past;
  return <Chip label={label} color={color} size="small" sx={{ fontWeight: 700, fontSize: 10 }} />;
};

const MetasPage = () => {
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [alert, setAlert] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // inline editing: { hotel_id, day_of_week, value }
  const [editing, setEditing] = useState(null);

  const showAlert = (message, severity = 'info') => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 3500);
  };

  // ─── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [hotelsRes, weeksRes] = await Promise.all([
          api.get('/hotels'),
          api.get('/metas/weeks'),
        ]);
        const hotelsData = (hotelsRes.data || []).filter((h) => h.status === 'active');
        const weeksData = weeksRes.data || [];
        setHotels(hotelsData);
        setWeeks(weeksData);

        const current = weeksData.find((w) => w.type === 'current') || weeksData[Math.floor(weeksData.length / 2)];
        if (current) await fetchGoals(current);
      } catch {
        showAlert('Error loading data', 'error');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const fetchGoals = useCallback(async (week) => {
    if (!week) return;
    setLoadingGoals(true);
    try {
      const res = await api.get('/metas/goals/by-week', { params: { week_start: week.week_start_date } });
      setGoals(res.data?.goals || []);
      setSelectedWeek(week);
    } catch {
      showAlert('Error loading weekly goals', 'error');
    } finally {
      setLoadingGoals(false);
    }
  }, []);

  // ─── Week navigation ───────────────────────────────────────────────────────
  const weekIndex = weeks.findIndex((w) => w.week_start_date === selectedWeek?.week_start_date);
  const goPrev = () => weekIndex > 0 && fetchGoals(weeks[weekIndex - 1]);
  const goNext = () => weekIndex < weeks.length - 1 && fetchGoals(weeks[weekIndex + 1]);

  const handleWeekSelect = (e) => {
    const w = weeks.find((w) => w.week_start_date === e.target.value);
    if (w) fetchGoals(w);
  };

  // ─── Goal lookup ───────────────────────────────────────────────────────────
  const getGoal = (hotelId, day) =>
    goals.find((g) => g.hotel_id === hotelId && g.day_of_week === day);

  const weeklyTotal = (hotelId) =>
    DAYS.reduce((sum, d) => sum + (getGoal(hotelId, d)?.target_amount || 0), 0);

  // ─── Editing ───────────────────────────────────────────────────────────────
  const isPast = selectedWeek?.type === 'past';

  const filteredHotels = hotels.filter((h) =>
    h.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const startEdit = (hotelId, day) => {
    if (isPast) return;
    const g = getGoal(hotelId, day);
    setEditing({ hotel_id: hotelId, day_of_week: day, value: g?.target_amount ? String(g.target_amount) : '' });
  };

  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing || !selectedWeek) return;
    const amount = parseFloat(editing.value);
    if (isNaN(amount) || amount < 0) {
      showAlert('Enter a valid amount (0 or more)', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (amount === 0 && editing.value !== '0') {
        const g = getGoal(editing.hotel_id, editing.day_of_week);
        if (g?.id) await api.delete(`/metas/goals/by-week/${g.id}`);
        setGoals((prev) => prev.filter((g) => !(g.hotel_id === editing.hotel_id && g.day_of_week === editing.day_of_week)));
      } else {
        const res = await api.post('/metas/goals/by-week/set', {
          week_start_date: selectedWeek.week_start_date,
          hotel_id: editing.hotel_id,
          day_of_week: editing.day_of_week,
          target_amount: amount,
        });

        setGoals((prev) => {
          const idx = prev.findIndex((g) => g.hotel_id === editing.hotel_id && g.day_of_week === editing.day_of_week);
          const updated = { hotel_id: editing.hotel_id, day_of_week: editing.day_of_week, target_amount: amount, id: res.data?.id };
          if (idx >= 0) {
            const arr = [...prev];
            arr[idx] = { ...arr[idx], ...updated };
            return arr;
          }
          return [...prev, updated];
        });
      }
      setEditing(null);
    } catch {
      showAlert('Error saving goal', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  const handleDeleteGoal = async (goalId, hotelId, day) => {
    if (isPast || !goalId) return;
    try {
      await api.delete(`/metas/goals/by-week/${goalId}`);
      setGoals((prev) => prev.filter((g) => !(g.hotel_id === hotelId && g.day_of_week === day)));
    } catch {
      showAlert('Error deleting goal', 'error');
    }
  };

  // ─── Clear week ────────────────────────────────────────────────────────────
  const handleClear = async () => {
    if (isPast || !selectedWeek) return;
    if (!clearConfirm) {
      setClearConfirm(true);
      setTimeout(() => setClearConfirm(false), 3000);
      return;
    }
    setClearing(true);
    try {
      await api.delete('/metas/goals/by-week/clear', { params: { week_start: selectedWeek.week_start_date } });
      setGoals([]);
      setClearConfirm(false);
      showAlert('All goals cleared', 'success');
    } catch {
      showAlert('Error clearing goals', 'error');
    } finally {
      setClearing(false);
    }
  };

  // ─── Copy to next week ─────────────────────────────────────────────────────
  const handleCopy = async () => {
    if (!selectedWeek) return;
    setCopying(true);
    try {
      const res = await api.post('/metas/goals/copy-to-next-week', { from_week_start: selectedWeek.week_start_date });
      const toWeek = res.data?.to_week;
      showAlert(`Copied to week of ${toWeek}`, 'success');
      const nextWeekObj = weeks.find((w) => w.week_start_date === toWeek);
      if (nextWeekObj) fetchGoals(nextWeekObj);
    } catch {
      showAlert('Error copying goals', 'error');
    } finally {
      setCopying(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={1} maxWidth="1100px" mx="auto">
      {/* Alert */}
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2, borderRadius: 2 }}>
          {alert.message}
        </Alert>
      )}

      {/* ── Week selector bar ── */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          backgroundColor: 'white',
          borderRadius: 2,
          p: 1.5,
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        {/* Left: chevron + date label + chevron */}
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={goPrev}
            disabled={weekIndex <= 0 || loadingGoals}
            size="small"
            sx={{ backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' }, borderRadius: 1.5 }}
          >
            <ChevronLeft size={18} />
          </IconButton>

          <Box display="flex" alignItems="center" gap={1}>
            <Calendar size={16} color="#667eea" />
            <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap', fontSize: 15 }}>
              {selectedWeek?.label || '—'}
            </Typography>
            {selectedWeek && <WeekBadge type={selectedWeek.type} />}
            {loadingGoals && <CircularProgress size={15} sx={{ ml: 0.5 }} />}
          </Box>

          <IconButton
            onClick={goNext}
            disabled={weekIndex >= weeks.length - 1 || loadingGoals}
            size="small"
            sx={{ backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' }, borderRadius: 1.5 }}
          >
            <ChevronRight size={18} />
          </IconButton>
        </Box>

        {/* Right: jump to week + gradient action buttons */}
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            select
            SelectProps={{ native: true }}
            size="small"
            label="Jump to week"
            value={selectedWeek?.week_start_date || ''}
            onChange={handleWeekSelect}
            sx={{ minWidth: 220, backgroundColor: 'white' }}
          >
            {weeks.map((w) => (
              <option key={w.week_start_date} value={w.week_start_date}>
                {w.label}
                {w.type === 'current' ? ' (current)' : ''}
              </option>
            ))}
          </TextField>

          {/* Refresh — gradient green */}
          <Button
            size="small"
            variant="contained"
            startIcon={loadingGoals ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <RefreshCw size={14} />}
            onClick={() => selectedWeek && fetchGoals(selectedWeek)}
            disabled={loadingGoals}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontSize: 12,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              boxShadow: 'none',
              '&:hover': { background: 'linear-gradient(135deg, #0e8a7e 0%, #2ed870 100%)', boxShadow: 'none' },
              '&.Mui-disabled': { background: '#e0e0e0', color: '#bbb' },
            }}
          >
            Refresh
          </Button>

          {/* Copy — gradient */}
          <Button
            size="small"
            variant="contained"
            startIcon={copying ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Copy size={14} />}
            onClick={handleCopy}
            disabled={!selectedWeek || copying || loadingGoals}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontSize: 12,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: 'none',
              '&:hover': { background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4395 100%)', boxShadow: 'none' },
              '&.Mui-disabled': { background: '#e0e0e0', color: '#bbb' },
            }}
          >
            Copy to next week
          </Button>

          {/* Clear — gradient red */}
          <Button
            size="small"
            variant="contained"
            startIcon={clearing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Trash2 size={14} />}
            onClick={handleClear}
            disabled={isPast || !selectedWeek || clearing || loadingGoals}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontSize: 12,
              background: 'linear-gradient(135deg, #f5576c 0%, #c0392b 100%)',
              boxShadow: 'none',
              '&:hover': { background: 'linear-gradient(135deg, #e04459 0%, #a93226 100%)', boxShadow: 'none' },
              '&.Mui-disabled': { background: '#e0e0e0', color: '#bbb' },
            }}
          >
            {clearConfirm ? 'Confirm clear?' : 'Clear week'}
          </Button>
        </Box>
      </Box>

      {/* Read-only notice */}
      {isPast && (
        <Alert severity="info" icon={<Calendar size={16} />} sx={{ mb: 2, borderRadius: 2, py: 0.5 }}>
          Past week — read only
        </Alert>
      )}

      {/* ── Search bar ── */}
      <Box mb={1}>
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          placeholder="Search hotels..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <Tooltip title="Clear">
                  <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                    <X size={14} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: 'white',
            borderRadius: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' } },
          }}
        />
      </Box>

      {/* ── Table ── */}
      {hotels.length === 0 ? (
        <Alert severity="info">No active hotels found.</Alert>
      ) : (
        <Box
          sx={{
            overflowX: 'auto',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            maxWidth: 1300,
            margin: '0 auto',
            width: '100%',
            maxHeight: '70vh',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '4px' },
          }}
        >
          <TableContainer component={Paper} sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {/* HOTEL — accent indigo */}
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 130,
                      px: 1,
                      py: 1,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#ffffff',
                      position: 'sticky',
                      top: 0,
                      left: 0,
                      zIndex: 4,
                      borderRight: '2px solid #c7d2fe',
                    }}
                  >
                    HOTEL
                  </TableCell>

                  {/* Day columns */}
                  {DAY_SHORT.map((d, i) => {
                    const date = getWeekDates(selectedWeek?.week_start_date)[i];
                    return (
                      <TableCell
                        key={d}
                        align="left"
                        sx={{
                          fontWeight: 'bold',
                          minWidth: 100,
                          px: 1,
                          py: 0.75,
                          backgroundColor: '#f5f5f5',
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                        }}
                      >
                        <Box display="flex" flexDirection="column" gap={0}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2 }}>{d}</Typography>
                          {date && (
                            <Typography sx={{ fontSize: 10, fontWeight: 400, color: 'text.secondary', lineHeight: 1.2 }}>
                              {fmtDay(date)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}

                  {/* WEEKLY — degradê roxo */}
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 100,
                      px: 1,
                      py: 1,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      borderLeft: '2px solid rgba(255,255,255,0.2)',
                    }}
                  >
                    WEEKLY
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredHotels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.disabled' }}>
                      No hotels match your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHotels.map((hotel) => {
                    const total = weeklyTotal(hotel.id);
                    return (
                      <TableRow
                        key={hotel.id}
                        sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                      >
                        {/* Hotel name */}
                        <TableCell
                          sx={{
                            px: 1,
                            py: 1,
                            fontWeight: 700,
                            fontSize: 13,
                            backgroundColor: '#eef2ff',
                            color: '#4338ca',
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            borderRight: '2px solid #c7d2fe',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {hotel.name}
                        </TableCell>

                        {/* Day cells */}
                        {DAYS.map((day) => {
                          const goal = getGoal(hotel.id, day);
                          const amount = goal?.target_amount || 0;
                          const isEditing =
                            editing?.hotel_id === hotel.id && editing?.day_of_week === day;

                          return (
                            <TableCell
                              key={day}
                              aligni="center"
                              sx={{
                                px: 1,
                                py: 0.75,
                                cursor: isPast ? 'default' : 'pointer',
                                '&:hover': isPast ? {} : { backgroundColor: 'rgba(102,126,234,0.06)' },
                              }}
                              onClick={() => !isEditing && startEdit(hotel.id, day)}
                            >
                              {isEditing ? (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <TextField
                                    autoFocus
                                    size="small"
                                    value={editing.value}
                                    onChange={(e) =>
                                      setEditing((prev) => ({ ...prev, value: e.target.value }))
                                    }
                                    onKeyDown={handleKey}
                                    inputProps={{
                                      style: { textAlign: 'center', fontSize: 12, padding: '4px 6px', width: 70 },
                                      type: 'number',
                                      min: 0,
                                    }}
                                    sx={{ '& fieldset': { borderColor: '#667eea' } }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <Tooltip title="Save (Enter)">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                                      disabled={saving}
                                    >
                                      {saving ? <CircularProgress size={12} /> : <Check size={12} />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel (Esc)">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                                    >
                                      <X size={12} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: amount > 0 ? 600 : 400,
                                      color: amount > 0 ? 'text.primary' : 'text.disabled',
                                    }}
                                  >
                                    {amount > 0
                                      ? new Intl.NumberFormat('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                          minimumFractionDigits: 0,
                                        }).format(amount)
                                      : '—'}
                                  </Typography>
                                  {!isPast && goal?.id && (
                                    <Tooltip title="Remove goal">
                                      <IconButton
                                        size="small"
                                        sx={{
                                          opacity: 0,
                                          '.MuiTableRow-root:hover &': { opacity: 1 },
                                          p: 0.25,
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteGoal(goal.id, hotel.id, day);
                                        }}
                                      >
                                        <X size={10} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          );
                        })}

                        {/* Weekly total */}
                        <TableCell
                          align="center"
                          sx={{
                            px: 1,
                            py: 1,
                            background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
                            borderLeft: '2px solid rgba(102,126,234,0.2)',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: total > 0 ? '#667eea' : 'text.disabled',
                            }}
                          >
                            {total > 0
                              ? new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                }).format(total)
                              : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default MetasPage;